import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '@/lib/utils';

interface PhotoUploadResponse {
  ok: true;
  propertyId: string;
  fileId: string;
  gpsLat: number;
  gpsLon: number;
  gpsCapturedAt: number | null;
  photoUrl: string;
}

interface PhotoUploadEntry {
  id: string;            // local key
  filename: string;
  status: 'uploading' | 'ok' | 'error';
  message?: string;
  propertyId?: string;
  gpsLat?: number;
  gpsLon?: number;
}

export default function Upload() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [photoLog, setPhotoLog] = useState<PhotoUploadEntry[]>([]);
  const photoInputRef = useRef<HTMLInputElement>(null);

  const handlePhotoFiles = useCallback(
    async (files: FileList | File[]) => {
      const arr = Array.from(files);
      if (!arr.length) return;
      setError(null);

      const entries: PhotoUploadEntry[] = arr.map((f, i) => ({
        id: `${Date.now()}-${i}`,
        filename: f.name,
        status: 'uploading',
      }));
      setPhotoLog((prev) => [...entries, ...prev]);

      await Promise.all(
        arr.map(async (file, i) => {
          const entryId = entries[i].id;
          try {
            const form = new FormData();
            form.append('file', file);
            const res = await fetch('/api/upload/photo', { method: 'POST', body: form });
            const data = await res.json();
            if (!res.ok) {
              setPhotoLog((prev) =>
                prev.map((e) =>
                  e.id === entryId
                    ? { ...e, status: 'error', message: (data as { message?: string })?.message || `HTTP ${res.status}` }
                    : e,
                ),
              );
              return;
            }
            const ok = data as PhotoUploadResponse;
            setPhotoLog((prev) =>
              prev.map((e) =>
                e.id === entryId
                  ? {
                      ...e,
                      status: 'ok',
                      propertyId: ok.propertyId,
                      gpsLat: ok.gpsLat,
                      gpsLon: ok.gpsLon,
                    }
                  : e,
              ),
            );
          } catch (e) {
            setPhotoLog((prev) =>
              prev.map((p) =>
                p.id === entryId ? { ...p, status: 'error', message: String(e) } : p,
              ),
            );
          }
        }),
      );

      queryClient.invalidateQueries({ queryKey: ['properties'] });
    },
    [queryClient],
  );

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Přidat nemovitost</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Vyfoť nemovitost v terénu (telefon zaznamená GPS) a nahraj fotky tady.
          Systém vytvoří nezatříděnou nemovitost, ke které pak v seznamu doplníš výpis z katastru.
        </p>
      </div>

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          if (e.dataTransfer.files?.length) handlePhotoFiles(e.dataTransfer.files);
        }}
        onClick={() => photoInputRef.current?.click()}
        className={cn(
          'rounded-lg border-2 border-dashed p-12 text-center cursor-pointer transition-colors',
          dragOver ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/30',
        )}
      >
        <input
          ref={photoInputRef}
          type="file"
          accept="image/jpeg,image/heic,image/heif,image/png"
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files?.length) handlePhotoFiles(e.target.files);
            e.target.value = '';
          }}
        />
        <div className="text-base font-medium">
          Přetáhni sem fotky, nebo klikni pro výběr
        </div>
        <div className="text-xs text-muted-foreground mt-1">
          JPEG / HEIC / PNG — fotka musí mít EXIF GPS (telefon → Camera → Poloha zapnutá).
          Pokud posíláš přes WhatsApp / Messenger, EXIF se odstraní — pošli originál ze složky Fotky.
        </div>
      </div>

      {photoLog.length > 0 && (
        <div className="rounded-lg border divide-y text-sm">
          {photoLog.map((e) => (
            <div key={e.id} className="px-3 py-2 flex items-center gap-3">
              <span
                className={cn(
                  'inline-block w-2 h-2 rounded-full shrink-0',
                  e.status === 'uploading' && 'bg-amber-500 animate-pulse',
                  e.status === 'ok' && 'bg-green-500',
                  e.status === 'error' && 'bg-red-500',
                )}
              />
              <div className="font-mono text-xs truncate flex-1">{e.filename}</div>
              {e.status === 'ok' && e.gpsLat != null && e.gpsLon != null && (
                <span className="text-xs text-muted-foreground">
                  {e.gpsLat.toFixed(5)}, {e.gpsLon.toFixed(5)}
                </span>
              )}
              {e.status === 'error' && (
                <span className="text-xs text-destructive max-w-[40%] truncate" title={e.message}>
                  {e.message}
                </span>
              )}
            </div>
          ))}
          <div className="px-3 py-2 text-right">
            <button
              onClick={() => navigate('/properties?status=unclassified')}
              className="text-xs underline text-primary"
            >
              Otevřít nezatříděné nemovitosti →
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="rounded-md border border-destructive bg-destructive/10 p-4 text-sm text-destructive">
          {error}
        </div>
      )}
    </div>
  );
}
